import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { CATEGORY_STYLES, PRIORITY_STYLES } from './notificationsData';

function formatTimestamp(iso) {
  const now = new Date();
  const date = new Date(iso);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 1) {
    return `Yesterday at ${date?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return date?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function NotificationCard({ notification, onMarkRead, onArchive, onRestore, onDelete, isArchiveView = false, userRole = 'investor' }) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  const catStyle = CATEGORY_STYLES?.[notification?.category] || CATEGORY_STYLES?.system;
  const priStyle = PRIORITY_STYLES?.[notification?.priority] || PRIORITY_STYLES?.Low;

  const leftBorderColor = notification?.priority === 'High' ? 'border-l-red-500'
    : notification?.isRead
    ? 'border-l-transparent' : 'border-l-primary';

  return (
    <div
      className={`relative bg-card rounded-xl border border-border border-l-4 ${leftBorderColor} shadow-sm transition-all duration-200 card-interactive ${
        hovered ? 'shadow-md' : ''
      } ${isArchiveView ? 'opacity-75' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Main row */}
      <div
        className="flex items-start gap-4 p-4 cursor-pointer"
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e?.key === 'Enter' && setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        {/* Category icon */}
        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${catStyle?.bg}`}>
          <Icon name={catStyle?.icon} size={18} className={catStyle?.text} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Unread dot */}
              {!notification?.isRead && !isArchiveView && (
                <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
              )}
              <h3 className={`font-caption text-sm leading-snug ${!notification?.isRead && !isArchiveView ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                {notification?.title}
              </h3>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Priority badge */}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${priStyle?.bg} ${priStyle?.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${priStyle?.dot}`} />
                {notification?.priority}
              </span>
              {/* Timestamp */}
              <span className="font-caption text-xs text-muted-foreground whitespace-nowrap">{formatTimestamp(notification?.timestamp)}</span>
              {/* Expand chevron */}
              <Icon
                name={expanded ? 'ChevronUp' : 'ChevronDown'}
                size={16}
                color="var(--color-muted-foreground)"
              />
            </div>
          </div>

          {/* Description preview */}
          {!expanded && (
            <p className="mt-1 font-caption text-xs text-muted-foreground line-clamp-2">{notification?.description}</p>
          )}

          {/* Asset chip */}
          {notification?.asset && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={(e) => {
                  e?.stopPropagation();
                  if (notification?.assetPath) navigate(notification?.assetPath);
                }}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${catStyle?.bg} ${catStyle?.text} border-transparent hover:border-current transition-colors`}
              >
                <Icon name="Link" size={10} />
                {notification?.asset}
              </button>
            </div>
          )}
        </div>

        {/* Hover action buttons */}
        <div
          className={`flex items-center gap-1 flex-shrink-0 transition-opacity duration-150 ${
            hovered ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={(e) => e?.stopPropagation()}
        >
          {!isArchiveView && (
            <>
              <button
                onClick={() => onMarkRead?.(notification?.id)}
                title={notification?.isRead ? 'Mark as unread' : 'Mark as read'}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name={notification?.isRead ? 'MailOpen' : 'Check'} size={14} />
              </button>
              <button
                onClick={() => onArchive?.(notification?.id)}
                title="Archive"
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="Archive" size={14} />
              </button>
            </>
          )}
          {isArchiveView && (
            <>
              <button
                onClick={() => onRestore?.(notification?.id)}
                title="Restore"
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-green-50 text-green-600 hover:text-green-700 transition-colors"
              >
                <Icon name="RotateCcw" size={14} />
              </button>
              {userRole === 'admin' && (
                <button
                  onClick={() => onDelete?.(notification?.id)}
                  title="Delete permanently"
                  className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-red-500 hover:text-red-600 transition-colors"
                >
                  <Icon name="Trash2" size={14} />
                </button>
              )}
            </>
          )}
          {notification?.cta && (
            <button
              onClick={() => navigate(notification?.cta?.path)}
              title="View details"
              className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-primary/8 text-primary transition-colors"
            >
              <Icon name="ArrowRight" size={14} />
            </button>
          )}
        </div>
      </div>
      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-border mt-0 pt-4">
          <p className="font-caption text-sm text-foreground leading-relaxed mb-4">{notification?.description}</p>

          {/* Metadata row */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${catStyle?.bg} ${catStyle?.text}`}>
              <Icon name={catStyle?.icon} size={12} />
              {catStyle?.label}
            </span>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${priStyle?.bg} ${priStyle?.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${priStyle?.dot}`} />
              {notification?.priority} Priority
            </span>
            <span className="font-caption text-xs text-muted-foreground">
              {new Date(notification?.timestamp)?.toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </span>
            {notification?.asset && (
              <button
                onClick={() => notification?.assetPath && navigate(notification?.assetPath)}
                className={`inline-flex items-center gap-1 text-xs font-medium ${catStyle?.text} hover:underline`}
              >
                <Icon name="ExternalLink" size={11} />
                {notification?.asset}
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {notification?.cta && (
              <button
                onClick={() => navigate(notification?.cta?.path)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {notification?.cta?.label}
                <Icon name="ArrowRight" size={14} />
              </button>
            )}
            {!isArchiveView && (
              <>
                <button
                  onClick={() => onMarkRead?.(notification?.id)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-muted-foreground text-sm hover:bg-muted transition-colors"
                >
                  <Icon name={notification?.isRead ? 'MailOpen' : 'Check'} size={14} />
                  {notification?.isRead ? 'Mark as unread' : 'Mark as read'}
                </button>
                <button
                  onClick={() => onArchive?.(notification?.id)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-muted-foreground text-sm hover:bg-muted transition-colors"
                >
                  <Icon name="Archive" size={14} />
                  Archive
                </button>
              </>
            )}
            {isArchiveView && (
              <>
                <button
                  onClick={() => onRestore?.(notification?.id)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-green-200 text-green-700 text-sm hover:bg-green-50 transition-colors"
                >
                  <Icon name="RotateCcw" size={14} />
                  Restore
                </button>
                {userRole === 'admin' && (
                  <button
                    onClick={() => onDelete?.(notification?.id)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm hover:bg-red-50 transition-colors"
                  >
                    <Icon name="Trash2" size={14} />
                    Delete permanently
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
